import {useAuthState} from "react-firebase-hooks/auth";
import {useCallback, useMemo} from "react";
import {signOut, User as FirebaseUser} from "firebase/auth";

import {auth} from "../../utils/firebase";
import {useQuery} from "@apollo/client";
import {GET_ME} from "../../graphql/queries/queries";
import {MeData} from "../../models/user";

type FirebaseStsTokenManager = {
	accessToken: string;
	refreshToken?: string;
	expirationTime?: number;
};

type NormalizedFirebaseUser = FirebaseUser & {
	stsTokenManager?: FirebaseStsTokenManager;
};

export const useAuthentication = () => {
	const [firebaseUser, firebaseLoading, firebaseError] = useAuthState(auth);
	const {data: meData, loading: meLoading} = useQuery<MeData>(GET_ME, {
		skip: !firebaseUser || firebaseUser.isAnonymous,
		fetchPolicy: "network-only",
	});

	const logout = useCallback(() => {
		return signOut(auth);
	}, []);

	const normalizedUser = useMemo<NormalizedFirebaseUser | null>(
		() => firebaseUser as NormalizedFirebaseUser | null,
		[firebaseUser]
	);

	const isAnonymousUser = useMemo<boolean>(() => Boolean(normalizedUser?.isAnonymous), [normalizedUser]);

	const isAuthenticated = useMemo<boolean>(
		() => Boolean(normalizedUser && !normalizedUser.isAnonymous),
		[normalizedUser]
	);

	const email = useMemo<string | null>(
		() => normalizedUser?.email ?? meData?.me?.email ?? null,
		[normalizedUser, meData]
	);

	return {
		user: normalizedUser,
		me: meData?.me,
		loading: firebaseLoading || meLoading,
		error: firebaseError,
		logout,
		isAuthenticated,
		isAnonymousUser,
		email,
	};
};
